import { trafficAssignmentsEn, trafficAssignmentsRu } from "./view/pages/administration/trafficAssignmentTranslations";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import interfaceEn from "./locales/interface.en.json";
import interfaceRu from "./locales/interface.ru.json";
import { banRuleEditorEn, banRuleEditorRu } from "./view/pages/administration/banRuleTranslations";
import { clientEarningsCalmEn, clientEarningsCalmRu } from "./view/pages/clientPage/subpages/clientEarningsCalmTranslations";

export const SUPPORTED_LANGUAGES = ["en", "ru"] as const;
export type AppLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const LANGUAGE_STORAGE_KEY = "crm.language";

const resources = {
	en: {
		translation: {
			interface: interfaceEn,
			dateRange: {
				start: "Start",
				end: "End",
				apply: "Apply",
				cancel: "Cancel",
				clear: "Clear",
				any: "Any dates",
				presets: "Quick periods",
				pickStart: "Select a start date",
				pickEnd: "Select an end date",
				days_one: "{{count}} day",
				days_other: "{{count}} days",
				previousMonth: "Previous month",
				nextMonth: "Next month",
				date: "Date",
				today: "Today",
				selectDate: "Select a date",
				month: "Month",
				year: "Year",
			},
			common: {
				close: "Close",
				refresh: "Refresh",
				retry: "Retry",
				loading: "Loading...",
				notSet: "Not set",
			},
			filePreview: {
				recordDetails: "Record details", singleRecord: "1 record · {{count}} fields", notProvided: "Not provided",
				groups: { chargeback: "Chargeback", payment: "Payment and card", merchant: "Merchant", settlement: "Settlement and reversal", response: "Merchant response", references: "References and identifiers", other: "Other fields" },
			},
			liveness: {
				period: "Period", period24: "24 hours", period4: "4 hours", period1: "1 hour",
				interval: "Interval: {{minutes}} min", details: "Interval details", expected: "Expected", successful: "Successful", failed: "Failed", missed: "Missed",
				openInterval: "Click an interval to view checks.", checkCount: "Checks: {{count}}", detailsError: "Could not load interval details.",
				emptyInterval: "No checks returned for this interval. Missed checks have no individual records.", timeout: "Request timed out",
				states: { success: "Success", failed: "Failed", missed: "Missed", pending: "In progress", degraded: "Degraded", unknown: "No data" },
				title: "Service status",
				unavailable: "Status unavailable",
				checking: "Checking services...",
				issues: "Services reporting issues",
				operational: "All services operational",
				partialData: "Some services have no data",
				last24Hours: "Last 24 hours",
				refreshStatus: "Refresh status",
				closeStatus: "Close service status",
				refreshError: "Could not refresh service status.",
				previousShown: "Previous checks are shown below.",
				tryAgain: "Try refreshing again.",
				hoursAgo24: "24h ago",
				hoursAgo12: "12h ago",
				now: "Now",
				operationalState: "Operational",
				failedState: "Failed",
				noData: "No data",
				error: "Error",
				refreshing: "Refreshing...",
				updated: "Updated {{time}}",
				notUpdated: "Not updated yet",
			},
			nav: {
				menu: "Menu",
				dashboard: "Dashboard",
				registration: "Registration",
				clients: "Clients",
				accountant: "Accountant",
				client: "Client",
				providers: "Providers",
				provider: "Payment provider",
				payouts: "Payouts",
				users: "Users",
				processing: "Processing",
				reports: "Reports",
				events: "Events",
				administration: "Administration",
				merchant: "Merchant",
				channel: "Channel",
				terminal: "Terminal",
			},
			administration: {
				partyUnavailable: "Name unavailable",
				recordDetails: "Rule event details", openRecord: "Open rule event {{id}}", emailAnalysis: "Analyze {{email}} · {{from}} – {{to}} · all currencies",
				checksTitle: "Rule checks", checkNumber: "Condition {{index}}", checkMatched: "Matched", checkNotMatched: "Not matched", checkNotReturned: "No result returned", checkUnknown: "Match status unavailable",
				observedValue: "Observed value", maximum: "max {{max}}", checkPeriod: "Rule check period", checkLimit: "{{status}}: {{value}} / max {{max}}",
				checkStatus: { completed: "Completed", failed: "Failed" }, checkJoin: { OR: "Conditions combined with OR", AND: "Conditions combined with AND" },
				blockReason: "Why the operation was blocked", matchedConditions: "Matched conditions", reasonLimit: "{{label}}: {{value}} recorded, with a maximum of {{max}}.", excessBy: "Above the limit by {{value}}.", maximumLabel: "Maximum allowed", fullRecord: "All record fields", checkError: "Rule check error",
				checkLogic: { OR: "Any one of these conditions can trigger the rule (OR).", AND: "All conditions must match to trigger the rule (AND)." },
				description: "Bans and rule activity.", blocks: "Rule blocks", results: "Rule activity",
				moreFilters: "More filters", apply: "Apply", reset: "Reset", all: "All", details: "Details",
				empty: "No records match the selected filters.", loadError: "Could not load rule activity. Try refreshing.",
				unrecognized: "The response could not be displayed as a list. The record format needs to be updated.",
				invalidFilters: "Check the filter values. IDs must be non-negative whole numbers.", invalidDates: "The end date must not be earlier than the start date.",
				perPage: "Rows per page", page: "Page {{page}}", pagination: "Rule activity pages", previous: "Previous page", next: "Next page",
				fields: { from: "From", to: "To", id: "ID", created_at: "Date (UTC)", client_id: "Client ID", mid: "MID", entity_type: "Entity type", entity_value: "Entity value", entity: "Entity", clientMerchant: "Client / MID", rule_name: "Rule", reference_id: "Reference ID", action: "Action", outcome: "Outcome", sandbox: "Sandbox", project_id: "Project ID", scope_index: "Scope index", rule_snapshot: "Rule snapshot", scope_results: "Check results", error_code: "Error code", error_message: "Error message" },
				action: { allow: "Allow", prevent: "Prevent", warn: "Warn", "not-performed": "Not performed" },
				outcome: { blocked: "Blocked", allowed: "Allowed", warned: "Warned", check_failed: "Check failed" },
			},
			trafficAssignments: trafficAssignmentsEn,
			banRules: {
				editor: banRuleEditorEn,
				editAction: "Edit", minorUnits: "Minor currency units",
				title: "Traffic Rules", create: "Create rule", details: "Traffic rule details", open: "Open rule {{id}}", pagination: "Traffic rule pages",
				empty: "No traffic rules yet.", loadError: "Could not load traffic rules. Try again.", saveUnavailable: "Saving is not available yet.",
				fields: { assignments: "Assignments", scope: "Conditions", updated_at: "Updated (UTC)", action: "Action", name: "Name", title: "Title", enabled: "Enabled", active: "Active", description: "Description", priority: "Priority" },
			},
			bans: {
				title: "Bans", add: "Add ban", remove: "Remove ban", details: "Ban details", detailsFor: "Ban details: {{value}}",
				forEntity: "Add ban: {{value}}",
				status: { title: "Existing bans", checking: "Checking bans…", banned: "Bans: {{count}}", unknown: "Ban status unknown", none: "No bans found for this object.", error: "Could not check existing bans. Refresh the status before adding a ban.", scopeHint: "Each ban applies only within its listed scope. Remove the specific ban you need.", duplicate: "A ban already exists in this scope. You can remove it above or choose another scope.", manageFor: "Manage bans: {{value}}" },
				cancel: "Cancel", saving: "Saving…", removing: "Removing…", saved: "Ban saved.", removed: "Ban removed.",
				scopeHint: "Applies to: {{scope}} #{{id}}.", removeHint: "Remove this ban? Other bans and rules may still block this object.",
				empty: "No bans match the selected filters.", pagination: "Ban list pages",
				fields: { entity_type: "Object type", entity_value: "Object", target_type: "Scope", target_id: "Scope ID", reason: "Reason", display_reason: "Display reason", search: "Search", id: "Ban ID", client_id: "Client ID", user_id: "User ID", user_name: "User", project_id: "Project ID", from: "From", to: "To", created_at: "Created (UTC)", updated_at: "Updated", actions: "Actions" },
				entities: { email: "Email", card: "Card", country: "Country", ip: "IP address", bin: "BIN", iban: "IBAN" },
				targets: { project: "Project", client: "Client", merchant: "Merchant" },
				errors: {
					invalidId: "Enter a valid positive whole-number ID.", invalidFilters: "Check the filter values.", invalidDates: "Check the dates. The end date must not precede the start date.", invalidEntity: "Enter an object value without control characters.",
					load: "Could not load bans. Try refreshing.", unrecognized: "The list format was not recognized. The response format needs to be clarified.", mutation: "Could not save the change.",
					unconfirmed: "The change could not be confirmed. Refresh the list before trying again.", contextChanged: "The user, project or access permissions changed. Reopen the Bans tab.",
				},
			},
			profileMenu: {
				profile: "Profile",
				logout: "Log out",
			},
			profile: {
				title: "Profile",
				preferences: "Preferences",
				language: "Interface language",
				languageHint: "The selected language is saved in this browser.",
				languages: { en: "English", ru: "Russian" },
				account: "Account",
				security: "Security",
				activity: "Activity",
				projects: "Projects",
				activeSessions: "Active Sessions",
				userId: "User ID",
				emailStatus: "Email status",
				phone: "Phone",
				type: "Type",
				branch: "Branch",
				signInAttempts: "Sign-in attempts",
				created: "Created",
				updated: "Updated",
				current: "Current",
				expires: "Expires",
				selected: "Selected",
				role: "Role {{id}}",
				name: "Name",
				changePassword: "Change password (leave empty to keep current)",
				currentPassword: "Current password",
				newPassword: "New password",
				confirmPassword: "Confirm new password",
				cancel: "Cancel",
				save: "Save",
				profileUpdated: "Profile updated!",
				passwordsMismatch: "Passwords do not match",
				twoFactorProtection: "Two-factor protection",
				protectionActive: "Protection is active",
				protectionActiveHint: "Disable it only if you are switching to another method or troubleshooting sign-in.",
				protectionEnabledHint: "Your account is protected with an extra verification step during sign-in.",
				protectionDisabledHint: "Add a second sign-in factor to reduce account takeover risk and protect project access.",
				disable2fa: "Disable 2FA",
				emailOtpSubtitle: "Best when you want the fastest setup with no extra app required.",
				emailOtpHighlight: "Codes are sent to your verified email address.",
				totpSubtitle: "Better for frequent sign-ins and works without waiting for email delivery.",
				totpHighlight: "Scan a QR code once, then confirm a 6-digit code.",
				emailOtpDescription: "Receive one-time passwords by email during sign-in.",
				totpDescription: "Scan a QR code in Google Authenticator and confirm the generated code.",
				continueSetup: "Continue setup",
				enable2fa: "Enable 2FA",
				totpSetup: "Google Authenticator setup",
				totpSetupHint: "Scan the QR code, then confirm the current 6-digit code from your authenticator app.",
				totpStep1: "1. Open Google Authenticator and add a new account.",
				totpStep2: "2. Scan this QR code or copy the setup link.",
				totpStep3: "3. Enter the current 6-digit code to finish setup.",
				authenticatorCode: "Authenticator code",
				copySetupLink: "Copy setup link",
				chooseAnotherMethod: "Choose another method",
				confirmTotp: "Confirm TOTP",
				authorizedDevices: "Authorized devices",
				authorizedDevicesHint: "Review the current browser session and terminate every other active device if needed.",
				logoutOtherSessions: "Log out other sessions ({{count}})",
				noOtherSessions: "No other active sessions",
				loadingSessions: "Loading sessions...",
				noSessions: "No active sessions were returned by the API.",
				emailOtpEnabled: "Email OTP enabled.",
				emailOtpEnabledProject: "Email OTP enabled for {{project}}.",
				totpEnabled: "Google Authenticator enabled.",
				twoFactorDisabled: "Two-factor authentication disabled.",
				setupLinkCopied: "Setup link copied.",
				copySetupLinkError: "Unable to copy setup link",
				otherSessionsTerminated: "Other sessions were terminated.",
			},
			stats: {
				titles: {
					overview: "Overview",
					topStatistics: "Top statistics",
					overallConversion: "Overall conversion",
					conversion: "Conversion",
					turnover: "Turnover",
					declines: "Declines",
					refunds: "Refunds",
					chargebacks: "Chargebacks",
					transactions: "Transactions",
					topChannels: "Top channels",
					topCountries: "Top countries",
					topBanks: "Top banks",
					topErrors: "Top errors",
					topStatuses: "Top statuses",
				},
				labels: { success: "Success", fail: "Fail", total: "Total" },
				chart: { series: "Series {{number}}", empty: "No chart data for the selected period" },
				fullReport: "See full report",
				distribution: { donut: "Donut chart", list: "List" },
				conversion: { donut: "Circular progress", linear: "Linear scale" },
				summary: { conversionHint: "Completed transactions / all transactions", failHint: "Fail includes all non-completed statuses." },
				subtitles: {
					conversion: "conversion",
					turnover: "Total turnover amount",
					declines: "Total failed amount",
					refunds: "Total refunds amount",
					chargebacks: "Total chargebacks amount",
				},
				allChannels: "All channels",
				channelNumber: "Channel #{{id}}",
				filters: {
					terminals: "Terminals",
					allTerminals: "All terminals",
					clients: "Clients",
					allClients: "All clients",
					search: "Search",
					noMatches: "Nothing found",
					loadError: "Could not load the list",
					selected_one: "{{count}} selected",
					selected_other: "{{count}} selected",
					instruments: {
						card: "Card",
						ap: "Apple Pay",
						gp: "Google Pay",
						ob: "Open Banking",
					},
				},
			},
			dashboard: {
				title: "{{project}} Dashboard",
				description: "Project overview, conversion, processing metrics, and payment activity.",
				tabs: { overview: "Overview", payments: "Payments", settings: "Settings", chargebacks: "Chargebacks" },
				periods: {
					today: "Today",
					yesterday: "Yesterday",
					last7Days: "Last 7 days",
					last30Days: "Last 30 days",
					thisMonth: "This month",
					lastMonth: "Last month",
					dateRange: "Date range",
					quickPeriod: "Quick period",
					customRange: "Custom range",
					locale: "en-GB",
				},
				allChannelsSummary: "The summary is available for all channels.",
				allChannelsHint: "Select “All channels”: statistics for an individual channel are not supported yet.",
				collectedAt: "collected at: {{date}}",
				generatedIn: "generated in: {{milliseconds}}ms",
				sessionRate: {
					title: "Payment rate",
					error: "Unable to load payment rate. Please try again.",
					asOf: "Updated (server time):",
					window: "Last {{minutes}} min",
					payments: "Payments",
					lastMinute: "Last full minute",
					currentMinute: "Current minute",
					partial: "Partial: {{seconds}}/60 s",
					average: "Average over {{minutes}} min",
					peak: "Window peak",
					perMinute: "/min",
					expand: "Expand payment rate",
					collapse: "Collapse payment rate",
					completed: "Completed",
					failed: "Failed",
					chart: "Payments per minute (server time)",
					chartType: "Chart type",
					lineChart: "Line chart",
					barChart: "Bar chart",
					areaChart: "Stacked area chart",
					empty: "No per-minute data",
				},
			},
			accountant: {
				title: "Accountant",
				calm: clientEarningsCalmEn,
				description: "Rolling reserves and profit for the selected period.",
				rollingTab: "Rolling Reserve",
				profitTab: "Profit",
				profitTitle: "Profit · Selected period",
				profitLoading: "Loading profit...",
				profitError: "Could not load profit for this period.",
				profitEmpty: "No profit data for this period.",
				source: "Source: {{source}}",
				locale: "en-GB",
				turnover: "Turnover",
				payableTurnover: "Payable Turnover",
				revenue: "Revenue",
				acquirerCost: "Acquirer cost",
				profit: "Profit",
				successful: "Successful",
				failed: "Failed",
				successRate: "Success rate",
				breakdown: "Profit breakdown",
				clients: "Clients ({{count}})",
				channels: "Channels ({{count}})",
				client: "Client",
				channel: "Channel",
				clientsTable: "Profit by client",
				channelsTable: "Profit by channel",
				selectClient: "Select {{name}}",
				selectedTotal: "Selected total ({{count}})",
				zeroAcquirerCostNote: "Acquirer cost is zero for this period because the gate does not populate interchange, scheme, or turnover costs yet. Profit currently equals revenue.",
				rollingTitle: "Rolling Reserve · Selected period",
				rollingTakenCount: "Reservations",
				rollingReleasedCount: "Releases",
				rollingTakenValue: "Reservations: {{value}}",
				rollingReleasedValue: "Releases: {{value}}",
				rollingMids: "MIDs: {{value}}",
				rollingShowMids: "Show MIDs of {{name}}",
				rollingHideMids: "Hide MIDs of {{name}}",
				rollingOpenClient: "Open client",
				rollingPartialBadge: "Incomplete data",
				rollingPartialTitle: "Incomplete period data.",
				rollingPartial: "Available coverage: {{from}} → {{to}}. Amounts outside this range may be understated.",
				rollingLoading: "Loading reserves...",
				rollingError: "Could not load rolling reserves for this period.",
				rollingEmpty: "No reserve data for this period.",
				reserved: "Reserved during period",
				released: "Released during period",
				held: "Held at period end",
				reserveAria: "{{currency}} reserves for selected period",
				earningsTitle: "Client earnings · Selected period",
				earningsLoading: "Loading client earnings...",
				earningsError: "Could not load client earnings for this period.",
				exportEarningsPdf: "Download earnings PDF (all currencies)",
				pdfGenerating: "Generating PDF...",
				pdfError: "Could not generate the PDF. Please try again.",
				reconciliation: "Reconciliation",
				earningsView: "Earnings basis",
				healthTitle: "Statement health",
				healthScope: "Current client · All dates",
				healthLoading: "Checking statements...",
				healthError: "Could not check statements. Please try again.",
				healthIssues: "Statement issues found",
				healthClean: "No statement issues found for this client",
				healthThreshold: "Stuck threshold: {{count}} days",
				healthAsOf: "Checked at (server time):",
				healthStuck: "Stuck statements",
				healthOverlapping: "Overlapping pairs",
				healthUnbilled: "Unbilled days",
				healthAge: "Age, days",
				healthPeriod: "Period (UTC)",
				healthPair: "Pair",
				healthDays: "Days",
				accrued: "Accrued",
				paid: "Paid",
				deferredTitle: "Deferred",
				deferredOut: "Billed after the period · Out",
				deferredIn: "Billed within the period · In",
				deferredOutDescription: "Created within the selected period, billed by a document generated after it.",
				deferredInDescription: "Created before the selected period, billed by a document generated within it.",
				deferredOutHint: "Arrives in the next payout.",
				deferredInHint: "Already paid — for earlier days. Not a problem, and not part of the red total.",
				deferredSubtitle: "the edges of the period",
				deferredNothing: "{{amount}} · nothing",
				byDocuments: "By documents",
				byDocumentsHint_one: "what was invoiced · {{count}} statement · reconciles with the bank",
				byDocumentsHint_other: "what was invoiced · {{count}} statements · reconciles with the bank",
				documentsRounding: "Net differs from its own breakdown by {{amount}}: every statement is an invoice and rounds each field to whole cents on its own, so {{count}} of them drift by a cent or two. The Net is the figure that was billed.",
				oct: "OCT payouts",
				byDocumentsSingle: "statement #{{id}} · {{type}} · {{status}}",
				overviewTurnoverDayHint: "{{payments}} payments created on {{day}}",
				shiftTitle: "Settlement shift",
				openPeriod: {
					title: "Period not closed — billed through {{through}}, {{days}} still open",
					titleNone: "Period not closed — no statement yet",
					text: "There has been no settlement and no calculation for these days yet. The figures below cover only the days already billed.",
					textNone: "There has been no settlement and no calculation for this period yet.",
					notBilled_one: "not billed yet · {{count}} payment",
					notBilled_other: "not billed yet · {{count}} payments",
				},
				shift: {
					caption: "<b>At T+1 the report for a day holds the previous day's payments.</b> That is the whole difference between {{here}} here and the {{above}} above — both are right.",
					captionShort: "<b>At T+1 the report for a day holds the previous day's payments.</b> That is why this block differs from the ones above — both are right.",
					inDay: "in from {{day}}",
					outDay: "out to {{day}}",
					inRange: "in from before the period",
					outRange: "out to after the period",
				},
				byReportDays: "By report days",
				byReportDaysHint_one: "{{count}} day · keyed by settlement — a different set of payments from the blocks above",
				byReportDaysHint_other: "{{count}} days · keyed by settlement — a different set of payments from the blocks above",
				outsideDaysList: "dates outside: {{days}}",
				noOutsideDays: "no dates outside",
				total: "Total",
				reconciledTitle: "Reconciled · every amount of the period is accounted for",
				formula: {
					created: "created",
					deferred: "deferred",
					notSettled: "not settled",
					earlier: "billed for earlier days",
					byDocuments: "by documents",
					outOfReports: "out of the reports",
					intoReports: "into the reports",
					beyond: "billed past the period's end",
					byReportDays: "by report days",
				},
				notSettled: {
					title: { empty: "Not settled", late: "Not settled · late", lost: "Not settled · lost", mixed: "Not settled", open: "Not settled · day not closed", stuck: "Not settled · stuck" },
					text: {
						empty: "Nothing stuck: every payment of the period has settled.",
						late: "Settlement not confirmed yet. <b>Will be paid out if the acquirer confirms</b> — the next daily document picks it up automatically.",
						lost: "Settlement declined by the acquirer. <b>This will never be paid out</b> — the money does not exist.",
						mixed: "Part may still arrive, part never will — <b>shown as separate rows, never one figure</b>.",
						open: "Payments of days no statement has closed yet. <b>Nothing to do</b> — they will be billed once the day closes.",
					},
					row: {
						late: "Not confirmed",
						lateLong: "Not confirmed · may still arrive",
						lost: "Declined",
						lostLong: "Declined · will never arrive",
						open: "Day not closed",
						openLong: "Day not closed · billed when it closes",
					},
					nothing: "{{amount}} · nothing stuck",
					stuckText: "Older than the client's settlement window and still not confirmed. <b>This is the one worth chasing.</b>",
					recentOnlyText: "Nothing stuck: every payment here is still inside the client's settlement window.",
					stuckRow: "Settlement not confirmed",
					recentTitle: "Awaiting settlement · normal",
					recentText_one: "created in the last {{count}} day — not yet due",
					recentText_other: "created in the last {{count}} days — not yet due",
					recentTextPlain: "still inside the settlement window — not yet due",
					recentShow_one: "Show {{count}} payment",
					recentShow_other: "Show {{count}} payments",
					threshold_one: "Stuck after {{count}} day",
					threshold_other: "Stuck after {{count}} days",
					thresholdClient_one: "Stuck after {{count}} day — the client's settlement window",
					thresholdClient_other: "Stuck after {{count}} days — the client's settlement window",
				},
				deferredCount: "Entries: {{count}}",
				deferredEmpty: "No amounts",
				deferredAtLeast: "{{currency}} · at least",
				deferredLowerBound: "Lower bound: these figures are incomplete while the period does not reconcile",
				overviewTurnover: "Overview turnover · period total",
				overviewTurnoverHint: "{{payments}} payments created {{from}} → {{to}} · the figure the Overview screen shows",
				settlementOpenDay: "day not closed",
				settlementStatus: {
					unknown: "settlement not confirmed",
					pending: "settlement in progress",
					failed: "settlement declined",
					error: "settlement error",
				},
				unsettledShown: "Showing {{shown}} of {{total}}",
				unsettledAge_zero: "waiting under a day",
				unsettledAge_one: "{{count}} day waiting",
				unsettledAge_other: "{{count}} days waiting",
				openTransaction: "Open transaction #{{id}}",
				notPaidOutTotal: "Not paid out for this period",
				notBilled: "Not billed",
				notBilledDescription: "Settled transactions without a billing document.",
				lateSettledTruncated: "The late-settled transaction list is partial. Amounts include all transactions.",
				notPaidOutBreakdownWithNotBilled: "{{deferred}} deferred + {{unsettled}} not settled + {{notBilled}} not billed = <b>{{total}}</b>",
				notPaidOutBreakdown: "{{deferred}} deferred + {{unsettled}} not settled = <b>{{total}}</b>",
				notPaidOutWithheld: "Total not paid out is <b>not shown</b> while the period does not reconcile — adding these tiles would understate it.",
				turnoverMismatch: "Does not reconcile — off by {{amount}}",
				turnoverMismatchDescription: "The figures below are incomplete, so they do not add up to the period's turnover. Do not use them as a breakdown of where the money went.",
				daysCovered: "Accrued activity covers {{count}} days",
				paidStatements: "Statements in period: {{count}}",
				paidByPeriod: "Payout period breakdown",
				paidInsidePeriod: "Net for the selected period",
				paidOutsidePeriod: "Net from outside the selected period",
				outsideDays: "Included dates outside the period: {{count}}",
				periodBreakdownDetails: "Metric breakdown",
				fees: "Fees",
				rollingTaken: "Rolling reserved",
				rollingReleased: "Rolling released",
				refunds: "Refunds",
				chargebacks: "Chargebacks",
				net: "Net",
				withdrawal: "Withdrawal fees",
				setup: "Setup fees",
				monthly: "Monthly fees",
				earningsByChannel: "Earnings by channel",
				noActivity: "No activity in this period.",
				channelsEmpty: "No channel data for this currency.",
				statements: "Paid statements",
				statement: "Statement ID",
				openStatement: "Open statement #{{id}}",
				type: "Type",
				status: "Status",
				period: "Period",
				coverage: "Coverage",
				fullyCovered: "Fully inside",
				partiallyCovered: "Partial",
				statementsEmpty: "No statements for this currency.",
				pendingEmpty: "No pending data",
				pendingAmount: "Pending: {{amount}} in statements not completed yet",
				uncoveredDays: "Uncovered days: {{count}}",
				fullyReconciled: "All days are covered by statements",
				refresh: "Refresh",
				retry: "Retry",
			},
			sharedEmails: {
				title: "Shared emails",
				overviewTitle: "Email overview",
				overviewIdle: "Shared emails and chargebacks",
				sharedTab: "Across merchants",
				chargebacksTab: "Chargebacks",
				chargebacksPeriod: "Last 3 months",
				chargebacksUnavailable: "Chargeback email data is unavailable.",
				chargebacksLoadError: "Could not load chargeback emails.",
				chargebacksCount: "Emails with chargebacks: {{count}}",
				chargebacksEmpty: "No emails with chargebacks for the last 3 months.",
				chargebacksSearch: "Search email",
				charged: "Charged",
				returned: "Returned",
				midsCount: "Merchants",
				idle: "Emails shared across merchants",
				search: "Email, client or merchant",
				clearSearch: "Clear search",
				matches: "Matching emails: {{count}} of {{total}}",
				noMatches: "No matching emails.",
				expand: "Expand email overview",
				collapse: "Collapse email overview",
				count: "Emails across merchants: {{count}}",
				shown: "Emails shown: {{count}} of {{total}}",
				truncated: "Partial list",
				empty: "No shared emails for the selected period.",
				unavailable: "Shared email data is unavailable.",
				loadError: "Could not load shared emails.",
				merchant: "Merchant",
				client: "Client",
				attempts: "Attempts",
				completed: "Completed",
				firstSeen: "First seen",
				lastSeen: "Last seen",
			},
			traffic: {
				controlDay: "Daily traffic monitoring",
				checking: "Checking",
				checkingTraffic: "Checking traffic...",
				loadError: "Loading error",
				normal: "Traffic is normal",
				warning: "Traffic requires attention",
				critical: "Sharp traffic change",
				unavailable: "No assessment",
				openDetails: "Open traffic monitoring details",
				refreshControl: "Refresh traffic monitoring",
				requestError: "Could not load traffic monitoring data.",
				comparison: "{{current}} compared with {{previous}}",
				datesPerClient: "Comparison dates are shown for each client",
				noProblems: "No deviations requiring attention were found.",
				sharpChange: "Sharp change",
				requiresAttention: "Requires attention",
				change: "Change",
				riseFromZero: "Increase from zero",
				transactions: "Transactions",
				dataAsOf: "Data as of {{date}} (server time)",
				details: "Traffic monitoring details",
				problems: "Traffic issues",
				increase: "Increase by {{percent}}%",
				decrease: "Decrease by {{percent}}%",
				noComparison: "No data for comparison",
				noChange: "No change",
				clientsWithDeviations: "Clients with deviations: {{count}}",
				noDailyDeviations: "No significant daily deviations found",
				insufficientData: "Not enough data to assess traffic",
			},
			interchange: {
				title: "IC++ fees",
				totalAria: "IC++ total",
				payment: "Payment",
				inEur: "In EUR",
				exchangeRate: "Exchange rate",
				totalFee: "Total IC++ fee",
				ofPaymentAmount: "of payment amount",
				groups: {
					interchange: { title: "Interchange", description: "Issuer bank fee" },
					scheme: { title: "Scheme fees", description: "Payment scheme fees" },
					switching: { title: "Switching fees", description: "Transaction processing" },
				},
				metadata: { basis: "Basis", source: "Source", note: "Note", id: "ID", service: "Service", eventId: "Event ID", rate: "Rate", fixed: "Fixed part", maximum: "Maximum", minimum: "Minimum" },
				indicative: "Indicative",
				feeDetails: "Fee terms and source",
				emptyCategory: "No fees in this category are included in the calculation.",
				loadError: "Could not load the IC++ calculation. Try refreshing the data.",
				loading: "Loading IC++ calculation...",
				displayError: "Could not display the IC++ calculation. The source response is available from the Raw button.",
				unavailable: "The IC++ calculation is unavailable for this payment.",
			},
			transactionAnalytics: {
				targets: { bank: "Bank", email: "Email", card: "Card" },
				analysisObject: "Analysis object",
				period: "Analysis period",
				fromDate: "From date",
				toDate: "To date",
				show: "Show",
				invalidPeriod: "Enter a valid period: the start date must not be later than the end date.",
				loadingTransaction: "Loading transaction data...",
				wrongProject: "Open the transaction in its project to view analytics.",
				analysisFor: "Analysis: {{name}}",
				periodField: "Period",
				currencyField: "Currency",
				loadError: "Could not load analytics.",
				loadingAnalysis: "Loading analytics...",
				prepareError: "Could not prepare analytics for the selected period. Try refreshing the data. Source data is available from the Raw button.",
				missing: { bank: "The bank is not specified in this transaction.", email: "The transaction does not contain the payer's email.", card: "The transaction does not contain a card token for analysis." },
				paymentCard: "Payment card",
			},
			emailInfo: {
				summary: "Email activity", loading: "Loading email activity…", loadError: "Could not load additional email information.",
				periodBasis: "Activity is grouped by payment creation date within the selected period.",
				attempts: "Attempts", completed: "Completed", failed: "Failed", abandoned: "Abandoned", charged: "Charged", returned: "Returned", refunded: "Refunded",
				cards: "Cards", mids_count: "MIDs", clients_count: "Clients", currencies: "Currencies", ips: "IP addresses", countries: "Countries", instruments: "Instruments", distinct_names: "Distinct names",
				first_seen: "First seen", last_seen: "Last seen", names: "Payer names", amounts: "Amounts by currency", currency: "Currency",
				completed_amount: "Completed amount", failed_amount: "Failed amount", charged_amount: "Charged amount", refunded_amount: "Refunded amount",
				clients: "Clients and MIDs", unnamedClient: "Unnamed client", unnamedCard: "Card number unavailable",
				identities: "Payer details", identity: "Payer", samplesNote: "Sample payer details returned for this MID; the list may not include every payment.",
				created_at: "Payment created", name: "Name", email: "Email", countryCode: "Country code", ip: "IP address", phone: "Phone", city: "City", state: "State / region", postalCode: "Postal code", addressLine1: "Address", addressLine2: "Address (line 2)", birthDate: "Date of birth",
				noAmounts: "No amounts returned.", noClients: "No clients returned.", noMids: "No MIDs returned.", noIdentities: "No payer details returned.", noCards: "No cards returned.",
				payments: "Payments", conversion: "Conversion", volume: "Volume", linked: "Linked data", activity: "Activity",
				attemptsCount: "Attempts: {{value}}", completedOf: "Completed {{completed}} of {{attempts}}", completedVolume: "completed", otherStatuses: "Other statuses",
				afterCompletion: "After completion", shareOfCompleted: "Share of completed", averagePayment: "Average completed payment",
				midsCount: "MIDs: {{value}}", activeDays: "{{value}} d", to: "to", flagged: "Needs attention",
				verdict: { danger: "Chargebacks found", warn: "Needs attention", ok: "No risk signals", empty: "No payments in this period" },
				verdictHint: { ok: "Checked: chargebacks, refunds, conversion, cards, clients, payer names and countries.", empty: "There is no activity for this email in the selected period." },
				signalsLabel: "Signals", signalHint: "Highlighted from {{value}}", conversionHint: "Below {{rate}} with {{attempts}} or more attempts",
				signals: { charged: "Charged: {{value}}", returned: "Returned: {{value}}", refunded: "Refunds: {{value}}", conversion: "Low conversion: {{value}}", cards: "Cards: {{value}}", clients_count: "Clients: {{value}}", distinct_names: "Payer names: {{value}}", countries: "Countries: {{value}}" },
			},
			bankAnalysis: {
				rules: "Rules", rulesEmpty: "No rule blocks for this email in the selected period.",
				titles: { bank: "Bank analysis · {{name}}", country: "Country analysis · {{name}}", email: "Email analysis · {{name}}", card: "Card analysis · {{name}}" },
				transactions: "Transactions",
				loadingDetails: "Loading details...",
				loadError: "Could not load analysis.",
				responseMismatch: "The response could not be matched to the selected object and period, or its metrics could not be recognized. The source response is available on the Raw tab.",
				crmResponse: "CRM response",
				waitingResponse: "Waiting for response...",
				rawUnavailable: "Source response is unavailable.",
				allCurrencies: "All currencies",
				periods: { today: "Today", yesterday: "Yesterday", dayBeforeYesterday: "Day before yesterday", thisMonth: "This month", lastMonth: "Last month", lastDays: "Last {{count}} days", lastDaysThroughYesterday: "Last {{count}} days through yesterday" },
				declines: "Declines",
				declinesAria: "declines",
				declinesCount: "Decline count",
				shareOfDeclines: "Share of all declines",
				totalDeclines: "total declines",
				countUnit: "pcs.",
				reasons: { title: "Decline reasons", reason: "Reason", google_pay_token: "Missing or invalid Google Pay token", wrong_params: "Invalid request parameters", wrong_pan: "BIN error: invalid card number", no_channel: "No active channel for terminal", fraud: "Suspected fraud", funds: "Insufficient funds or credit limit exceeded", conditions: "Card restrictions imposed by the bank", security: "Declined due to security requirements", cardholder: "Blocked by cardholder — contact required", no_card: "Card not found during authentication", pending: "Payment is still processing", invalid_card: "Invalid card number", expired_card: "Card has expired", "3ds": "3-D Secure authentication failed" },
				actions: { google_pay_token: "Check that the Google Pay token is obtained and passed in the payment request: CRM reports a missing or invalid token.", wrong_params: "Check required payment request parameters and their format.", wrong_pan: "Check the card number and the parameters passed for BIN detection.", no_channel: "Check active channels and terminal routing settings.", fraud: "Check the antifraud triggers at the provider and bank. This decline reason alone does not confirm fraud.", funds: "Check repeat payment attempts: the customer may need to add funds or use another card.", conditions: "Ask the provider or bank which card restrictions caused the declines.", security: "Ask the provider or bank which security checks are declining payments.", "3ds": "Check the 3-D Secure flow and the bank authentication service responses.", pending: "Check final statuses: CRM included payments that are still processing among declines.", fallback: "Review decline messages and repeat card attempts in the details below." },
				summary: { noDeclines: "No declines were found for the selected period.", noBreakdown: "CRM returned {{total}} declines. The reason breakdown is unavailable.", topReason: "The most common response reason is “{{reason}}”: {{count}} of {{total}} declines ({{share}})." },
				countries: "Countries",
				country: "Country",
				banks: "Banks",
				bank: "Bank",
				otherCountries: "Other countries",
				otherBanks: "Other banks",
				otherReasons: "Other reasons",
				notProvided: "Not provided in response",
				countryNotProvided: "Country not provided",
				bankNotProvided: "Bank not provided",
				cardNotProvided: "Card not provided",
				emailNotProvided: "Email not provided",
				allCountries: "All countries ({{count}})",
				allBanks: "All banks ({{count}})",
				allReasons: "All reasons ({{count}})",
				collapse: "Collapse",
				noPeriodDeclines: "There were no declines in the selected period.",
				highFrequency: "High decline frequency",
				requiresAttention: "Requires attention",
				highFrequencyAria: "high decline frequency",
				requiresAttentionAria: "requires attention",
				cardsWithDeclines: "Cards with declines",
				emailsWithDeclines: "Emails with declines",
				card: "Card",
				email: "Email",
				attentionCount: "Require attention: {{count}}",
				noThresholdItems: "no items reached the threshold",
				periodUnknown: "period not determined",
				crmItems: "CRM response: {{count}} items.",
				attentionThreshold: "Attention threshold: at least {{count}} declines over {{days}} days.",
				periodThresholdUnavailable: "Could not determine the period for repeat assessment.",
				noCardsAtThreshold: "No cards in the returned top list reached the attention threshold.",
				noEmailsAtThreshold: "No emails in the returned top list reached the attention threshold.",
				unknownDeclines: "{{label}}: {{count}} declines. They cannot be attributed to a single object.",
				attentionOnly: "Only requiring attention",
				showFullTop: "Show full top ({{count}})",
				partialReasons: "The breakdown contains reasons for {{count}} of {{total}} declines. CRM did not provide the remaining reasons.",
			},
			analysis: {
				title: "Payment analysis",
				filters: {
					category: "Filter by finding type", severity: "Filter by severity",
					all: "All types", bank: "Banks", country: "Countries", channel: "Payment channels", email: "Emails", technical: "Technical errors", other: "Other findings",
					allLevels: "All severities", critical: "Critical", warning: "Warnings", info: "Observations / limited data",
					matches: "Found {{count}} of {{total}}", reset: "Reset filters", empty: "No findings match these filters.",
				},
				loadError: "Load error",
				ok: "Everything is fine",
				warning: "Requires attention",
				critical: "Problems found",
				unavailable: "Assessment is not available yet",
				analyzing: "Analyzing statistics...",
				noSignificantIssues: "No significant deviations found",
				expand: "Expand",
				collapse: "Collapse",
				expandRules: "Expand rule analysis",
				collapseRules: "Collapse rule analysis",
				observations: "Observations",
				criticalLabel: "Critical",
				warningLabel: "Attention",
				infoLabel: "Observation",
				smallSample: "Limited data",
				changeFromPrevious: "Change from the previous period",
				decreasePoints: "Decrease by {{value}} pp",
				belowTarget: "against an 80% target",
				conversion: "conversion",
				paymentAttempts: "payment attempts",
				fewerThan30: "fewer than 30",
				errors: "errors",
				showTechnicalErrors: "Show technical errors",
				openAnalysis: "Open analysis for {{name}}",
				openAnalysisBank: "Open bank analysis for {{name}}",
				openAnalysisCountry: "Open country analysis for {{name}}",
				openAnalysisEmail: "Open email analysis for {{name}}",
				technicalErrors: "Technical errors",
				totalMessages: "Total messages: {{count}}",
				statisticsMessage: "Statistics message",
				count: "Count",
				technicalCategories: {
					google_pay_token: "Invalid Google Pay token format",
					routing: "No active channel for terminal",
					xml: "XML parsing errors in payment integration",
				},
				redactedEmail: "[hidden]",
				redactedLink: "[link]",
				redactedNumber: "[number]",
				technicalErrorsHint: "Errors from the same statistics that produced the warning. Messages may be shortened; individual transactions are not included in this data.",
				moreIssues: "{{count}} more issues",
				criticalPrefix: "Critical",
				noIssues: "No significant deviations",
				observationAvailable: "observation available",
				showAll: "Show all ({{count}})",
				collapseToTen: "Collapse to 10",
				observation: "Observation.",
			},
		},
	},
	ru: {
		translation: {
			interface: interfaceRu,
			dateRange: {
				start: "Начало",
				end: "Конец",
				apply: "Применить",
				cancel: "Отмена",
				clear: "Сбросить",
				any: "Любые даты",
				presets: "Быстрый выбор",
				pickStart: "Выберите дату начала",
				pickEnd: "Выберите дату окончания",
				days_one: "{{count}} день",
				days_few: "{{count}} дня",
				days_many: "{{count}} дней",
				days_other: "{{count}} дня",
				previousMonth: "Предыдущий месяц",
				nextMonth: "Следующий месяц",
				date: "Дата",
				today: "Сегодня",
				selectDate: "Выберите дату",
				month: "Месяц",
				year: "Год",
			},
			common: {
				close: "Закрыть",
				refresh: "Обновить",
				retry: "Повторить",
				loading: "Загрузка...",
				notSet: "Не указано",
			},
			filePreview: {
				recordDetails: "Детали записи", singleRecord: "1 запись · полей: {{count}}", notProvided: "Не указано",
				groups: { chargeback: "Чарджбэк", payment: "Платёж и карта", merchant: "Мерчант", settlement: "Расчёты и отмена", response: "Ответ мерчанта", references: "Ссылки и идентификаторы", other: "Другие поля" },
			},
			liveness: {
				period: "Период", period24: "24 часа", period4: "4 часа", period1: "Час",
				interval: "Интервал: {{minutes}} мин", details: "Детали интервала", expected: "Ожидалось", successful: "Успешных", failed: "Ошибок", missed: "Пропусков",
				openInterval: "Нажмите на интервал, чтобы посмотреть проверки.", checkCount: "Проверок: {{count}}", detailsError: "Не удалось загрузить детали интервала.",
				emptyInterval: "За этот интервал проверки не получены. У пропущенных проверок нет отдельных записей.", timeout: "Превышено время ожидания",
				states: { success: "Успешно", failed: "Ошибки", missed: "Пропуски", pending: "В процессе", degraded: "Ухудшение", unknown: "Нет данных" },
				title: "Статус сервисов",
				unavailable: "Статус недоступен",
				checking: "Проверяем сервисы...",
				issues: "Обнаружены проблемы в сервисах",
				operational: "Все сервисы работают",
				partialData: "По некоторым сервисам нет данных",
				last24Hours: "Последние 24 часа",
				refreshStatus: "Обновить статус",
				closeStatus: "Закрыть статус сервисов",
				refreshError: "Не удалось обновить статус сервисов.",
				previousShown: "Ниже показаны предыдущие проверки.",
				tryAgain: "Попробуйте обновить ещё раз.",
				hoursAgo24: "24 ч назад",
				hoursAgo12: "12 ч назад",
				now: "Сейчас",
				operationalState: "Работает",
				failedState: "Ошибка",
				noData: "Нет данных",
				error: "Ошибка",
				refreshing: "Обновление...",
				updated: "Обновлено {{time}}",
				notUpdated: "Ещё не обновлялось",
			},
			nav: {
				menu: "Меню",
				dashboard: "Дашборд",
				registration: "Регистрация",
				clients: "Клиенты",
				accountant: "Бухгалтерия",
				client: "Клиент",
				providers: "Провайдеры",
				provider: "Платёжный провайдер",
				payouts: "Выплаты",
				users: "Пользователи",
				processing: "Процессинг",
				reports: "Отчёты",
				events: "События",
				administration: "Администрирование",
				merchant: "Мерчант",
				channel: "Канал",
				terminal: "Терминал",
			},
			administration: {
				partyUnavailable: "Название недоступно",
				recordDetails: "Срабатывание правила", openRecord: "Открыть срабатывание {{id}}", emailAnalysis: "Аналитика {{email}} · {{from}} – {{to}} · все валюты",
				checksTitle: "Проверки правила", checkNumber: "Условие {{index}}", checkMatched: "Сработало", checkNotMatched: "Не сработало", checkNotReturned: "Результат не передан", checkUnknown: "Статус срабатывания не передан",
				observedValue: "Фактическое значение", maximum: "максимум {{max}}", checkPeriod: "Период проверки правила", checkLimit: "{{status}}: {{value}} / максимум {{max}}",
				checkStatus: { completed: "Успешные", failed: "Неуспешные" }, checkJoin: { OR: "Условия объединены через ИЛИ (OR)", AND: "Условия объединены через И (AND)" },
				blockReason: "Почему операция заблокирована", matchedConditions: "Сработавшие условия", reasonLimit: "{{label}}: зафиксировано {{value}} при максимуме {{max}}.", excessBy: "Превышение на {{value}}.", maximumLabel: "Допустимый максимум", fullRecord: "Все поля записи", checkError: "Ошибка проверки правила",
				checkLogic: { OR: "Для срабатывания правила достаточно одного из условий (ИЛИ).", AND: "Для срабатывания правила должны выполниться все условия (И)." },
				description: "Баны и журнал срабатывания правил.", blocks: "Блокировки по правилам", results: "Срабатывания правил",
				moreFilters: "Другие фильтры", apply: "Применить", reset: "Сбросить", all: "Все", details: "Подробнее",
				empty: "По выбранным фильтрам записей нет.", loadError: "Не удалось загрузить срабатывания правил. Попробуйте обновить список.",
				unrecognized: "Не удалось отобразить ответ в виде списка. Требуется уточнить формат записей.",
				invalidFilters: "Проверьте фильтры. Идентификаторы должны быть целыми неотрицательными числами.", invalidDates: "Дата окончания не должна быть раньше даты начала.",
				perPage: "Записей на странице", page: "Страница {{page}}", pagination: "Страницы срабатываний правил", previous: "Предыдущая страница", next: "Следующая страница",
				fields: { from: "С", to: "По", id: "ID", created_at: "Дата (UTC)", client_id: "ID клиента", mid: "MID", entity_type: "Тип объекта", entity_value: "Значение объекта", entity: "Объект проверки", clientMerchant: "Клиент / MID", rule_name: "Правило", reference_id: "Референс", action: "Действие", outcome: "Результат", sandbox: "Тестовый режим", project_id: "ID проекта", scope_index: "Индекс области", rule_snapshot: "Настройки правила на момент проверки", scope_results: "Результаты проверок", error_code: "Код ошибки", error_message: "Сообщение об ошибке" },
				action: { allow: "Разрешить", prevent: "Блокировать", warn: "Предупредить", "not-performed": "Не выполнено" },
				outcome: { blocked: "Заблокировано", allowed: "Разрешено", warned: "Предупреждение", check_failed: "Ошибка проверки" },
			},
			trafficAssignments: trafficAssignmentsRu,
			banRules: {
				editor: banRuleEditorRu,
				editAction: "Редактировать", minorUnits: "Минимальные единицы валюты",
				title: "Правила трафика", create: "Создать правило", details: "Правило трафика", open: "Открыть правило {{id}}", pagination: "Страницы Traffic Rules",
				empty: "Правил трафика пока нет.", loadError: "Не удалось загрузить правила трафика. Попробуйте ещё раз.", saveUnavailable: "Сохранение пока недоступно.",
				fields: { assignments: "Привязки", scope: "Условия", updated_at: "Обновлено (UTC)", action: "Действие", name: "Название", title: "Название", enabled: "Включено", active: "Активно", description: "Описание", priority: "Приоритет" },
			},
			bans: {
				title: "Баны", add: "Добавить бан", remove: "Снять бан", details: "Детали бана", detailsFor: "Детали бана: {{value}}",
				forEntity: "Добавить бан: {{value}}",
				status: { title: "Установленные баны", checking: "Проверка банов…", banned: "Есть баны: {{count}}", unknown: "Статус банов неизвестен", none: "Для этого объекта баны не найдены.", error: "Не удалось проверить установленные баны. Обновите статус перед добавлением бана.", scopeHint: "Каждый бан действует в указанной области. Можно снять конкретный бан.", duplicate: "В этой области уже стоит бан. Его можно снять выше или выбрать другую область.", manageFor: "Управление банами: {{value}}" },
				cancel: "Отмена", saving: "Сохранение…", removing: "Снятие…", saved: "Бан сохранён.", removed: "Бан снят.",
				scopeHint: "Область действия: {{scope}} #{{id}}.", removeHint: "Снять этот бан? Другие баны и правила могут по-прежнему блокировать этот объект.",
				empty: "По выбранным фильтрам банов нет.", pagination: "Страницы списка банов",
				fields: { entity_type: "Тип объекта", entity_value: "Объект", target_type: "Область действия", target_id: "ID области", reason: "Причина", display_reason: "Отображаемая причина", search: "Поиск", id: "ID бана", client_id: "ID клиента", user_id: "ID пользователя", user_name: "Пользователь", project_id: "ID проекта", from: "С", to: "По", created_at: "Создан (UTC)", updated_at: "Обновлён", actions: "Действия" },
				entities: { email: "Эл. почта", card: "Карта", country: "Страна", ip: "IP-адрес", bin: "BIN", iban: "IBAN" },
				targets: { project: "Проект", client: "Клиент", merchant: "Мерчант" },
				errors: {
					invalidId: "Укажите корректный целый положительный ID.", invalidFilters: "Проверьте значения фильтров.", invalidDates: "Проверьте даты. Дата окончания не должна быть раньше даты начала.", invalidEntity: "Укажите значение объекта без управляющих символов.",
					load: "Не удалось загрузить баны. Попробуйте обновить список.", unrecognized: "Не удалось распознать список. Требуется уточнить формат ответа.", mutation: "Не удалось сохранить изменение.",
					unconfirmed: "Не удалось подтвердить изменение. Обновите список перед повторной попыткой.", contextChanged: "Пользователь, проект или права доступа изменились. Откройте вкладку банов заново.",
				},
			},
			profileMenu: {
				profile: "Профиль",
				logout: "Выйти",
			},
			profile: {
				title: "Профиль",
				preferences: "Настройки",
				language: "Язык интерфейса",
				languageHint: "Выбранный язык сохраняется в этом браузере.",
				languages: { en: "Английский", ru: "Русский" },
				account: "Аккаунт",
				security: "Безопасность",
				activity: "Активность",
				projects: "Проекты",
				activeSessions: "Активные сессии",
				userId: "ID пользователя",
				emailStatus: "Статус email",
				phone: "Телефон",
				type: "Тип",
				branch: "Филиал",
				signInAttempts: "Попытки входа",
				created: "Создано",
				updated: "Обновлено",
				current: "Текущая",
				expires: "Истекает",
				selected: "Выбрано",
				role: "Роль {{id}}",
				name: "Имя",
				changePassword: "Смена пароля (оставьте поля пустыми, чтобы сохранить текущий)",
				currentPassword: "Текущий пароль",
				newPassword: "Новый пароль",
				confirmPassword: "Подтвердите новый пароль",
				cancel: "Отмена",
				save: "Сохранить",
				profileUpdated: "Профиль обновлён!",
				passwordsMismatch: "Пароли не совпадают",
				twoFactorProtection: "Двухфакторная защита",
				protectionActive: "Защита включена",
				protectionActiveHint: "Отключайте её только при смене метода или устранении проблем со входом.",
				protectionEnabledHint: "Аккаунт защищён дополнительным этапом проверки при входе.",
				protectionDisabledHint: "Добавьте второй фактор входа, чтобы снизить риск захвата аккаунта и защитить доступ к проекту.",
				disable2fa: "Отключить 2FA",
				emailOtpSubtitle: "Самая быстрая настройка без дополнительного приложения.",
				emailOtpHighlight: "Коды отправляются на подтверждённый email.",
				totpSubtitle: "Удобнее для частых входов и не требует ожидания письма.",
				totpHighlight: "Один раз отсканируйте QR-код, затем подтвердите 6-значный код.",
				emailOtpDescription: "Получайте одноразовые пароли по email при входе.",
				totpDescription: "Отсканируйте QR-код в Google Authenticator и подтвердите созданный код.",
				continueSetup: "Продолжить настройку",
				enable2fa: "Включить 2FA",
				totpSetup: "Настройка Google Authenticator",
				totpSetupHint: "Отсканируйте QR-код, затем подтвердите текущий 6-значный код из приложения-аутентификатора.",
				totpStep1: "1. Откройте Google Authenticator и добавьте новый аккаунт.",
				totpStep2: "2. Отсканируйте QR-код или скопируйте ссылку настройки.",
				totpStep3: "3. Введите текущий 6-значный код для завершения настройки.",
				authenticatorCode: "Код аутентификатора",
				copySetupLink: "Скопировать ссылку",
				chooseAnotherMethod: "Выбрать другой метод",
				confirmTotp: "Подтвердить TOTP",
				authorizedDevices: "Авторизованные устройства",
				authorizedDevicesHint: "Проверьте текущую сессию браузера и при необходимости завершите сессии на остальных устройствах.",
				logoutOtherSessions: "Завершить другие сессии ({{count}})",
				noOtherSessions: "Других активных сессий нет",
				loadingSessions: "Загрузка сессий...",
				noSessions: "API не вернул активных сессий.",
				emailOtpEnabled: "Email OTP включён.",
				emailOtpEnabledProject: "Email OTP включён для {{project}}.",
				totpEnabled: "Google Authenticator включён.",
				twoFactorDisabled: "Двухфакторная аутентификация отключена.",
				setupLinkCopied: "Ссылка настройки скопирована.",
				copySetupLinkError: "Не удалось скопировать ссылку настройки",
				otherSessionsTerminated: "Другие сессии завершены.",
			},
			stats: {
				titles: {
					overview: "Обзор",
					topStatistics: "Топ статистики",
					overallConversion: "Общая конверсия",
					conversion: "Конверсия",
					turnover: "Оборот",
					declines: "Отказы",
					refunds: "Возвраты",
					chargebacks: "Чарджбэки",
					transactions: "Транзакции",
					topChannels: "Топ каналов",
					topCountries: "Топ стран",
					topBanks: "Топ банков",
					topErrors: "Топ ошибок",
					topStatuses: "Топ статусов",
				},
				labels: { success: "Успешно", fail: "Отказ", total: "Всего" },
				chart: { series: "Ряд {{number}}", empty: "Нет данных графика за выбранный период" },
				fullReport: "Полный отчёт",
				distribution: { donut: "Кольцевой график", list: "Список" },
				conversion: { donut: "Круговой прогресс", linear: "Линейная шкала" },
				summary: { conversionHint: "Успешные транзакции / все транзакции", failHint: "В отказах учтены все операции, не завершённые успешно." },
				subtitles: {
					conversion: "конверсия",
					turnover: "Общая сумма оборота",
					declines: "Общая сумма отказов",
					refunds: "Общая сумма возвратов",
					chargebacks: "Общая сумма чарджбэков",
				},
				allChannels: "Все каналы",
				channelNumber: "Канал №{{id}}",
				filters: {
					terminals: "Терминалы",
					allTerminals: "Все терминалы",
					clients: "Клиенты",
					allClients: "Все клиенты",
					search: "Поиск",
					noMatches: "Ничего не найдено",
					loadError: "Не удалось загрузить список",
					selected_one: "Выбран {{count}}",
					selected_few: "Выбрано {{count}}",
					selected_many: "Выбрано {{count}}",
					selected_other: "Выбрано {{count}}",
					instruments: {
						card: "Карта",
						ap: "Apple Pay",
						gp: "Google Pay",
						ob: "Open Banking",
					},
				},
			},
			dashboard: {
				title: "Дашборд {{project}}",
				description: "Обзор проекта, конверсия, показатели процессинга и платёжная активность.",
				tabs: { overview: "Обзор", payments: "Платежи", settings: "Настройки", chargebacks: "Чарджбэки" },
				periods: {
					today: "Сегодня",
					yesterday: "Вчера",
					last7Days: "Последние 7 дней",
					last30Days: "Последние 30 дней",
					thisMonth: "Этот месяц",
					lastMonth: "Прошлый месяц",
					dateRange: "Диапазон дат",
					quickPeriod: "Быстрый период",
					customRange: "Свой период",
					locale: "ru-RU",
				},
				allChannelsSummary: "Резюме доступно для всех каналов.",
				allChannelsHint: "Выберите «Все каналы»: статистика отдельного канала пока не поддерживается.",
				collectedAt: "собрано: {{date}}",
				generatedIn: "сформировано за {{milliseconds}}мс",
				sessionRate: {
					title: "Частота платежей",
					error: "Не удалось загрузить частоту платежей. Попробуйте ещё раз.",
					asOf: "Обновлено (время сервера):",
					window: "Последние {{minutes}} мин",
					payments: "Платежи",
					lastMinute: "Последняя минута",
					currentMinute: "Текущая минута",
					partial: "Неполная: {{seconds}}/60 с",
					average: "Среднее за {{minutes}} мин",
					peak: "Пик за окно",
					perMinute: "/мин",
					expand: "Развернуть темп платежей",
					collapse: "Свернуть темп платежей",
					completed: "Завершено",
					failed: "Неуспешно",
					chart: "Платежи по минутам (время сервера)",
					chartType: "Вид графика",
					lineChart: "Линейный график",
					barChart: "Столбчатый график",
					areaChart: "График с накоплением",
					empty: "Нет поминутных данных",
				},
			},
			accountant: {
				title: "Бухгалтерия",
				calm: clientEarningsCalmRu,
				description: "Роллинги и прибыль за выбранный период.",
				rollingTab: "Страховой резерв",
				profitTab: "Прибыль",
				profitTitle: "Прибыль · Выбранный период",
				profitLoading: "Загружаем прибыль…",
				profitError: "Не удалось загрузить прибыль за этот период.",
				profitEmpty: "За этот период данных о прибыли нет.",
				source: "Источник: {{source}}",
				locale: "ru-RU",
				turnover: "Оборот",
				payableTurnover: "Payable Turnover",
				revenue: "Выручка",
				acquirerCost: "Расходы эквайера",
				profit: "Прибыль",
				successful: "Успешные",
				failed: "Неуспешные",
				successRate: "Проходимость",
				breakdown: "Детализация прибыли",
				clients: "Клиенты ({{count}})",
				channels: "Каналы ({{count}})",
				client: "Клиент",
				channel: "Канал",
				clientsTable: "Прибыль по клиентам",
				channelsTable: "Прибыль по каналам",
				selectClient: "Выбрать {{name}}",
				selectedTotal: "Итого по выбранным ({{count}})",
				zeroAcquirerCostNote: "Расходы эквайера за этот период равны нулю: gate пока не передаёт interchange, scheme и turnover costs. Поэтому прибыль сейчас равна выручке.",
				rollingTitle: "Rolling Reserve · Выбранный период",
				rollingTakenCount: "Резервирований",
				rollingReleasedCount: "Освобождений",
				rollingTakenValue: "Резервирований: {{value}}",
				rollingReleasedValue: "Освобождений: {{value}}",
				rollingMids: "MID: {{value}}",
				rollingShowMids: "Показать MID клиента {{name}}",
				rollingHideMids: "Скрыть MID клиента {{name}}",
				rollingOpenClient: "Открыть клиента",
				rollingPartialBadge: "Неполные данные",
				rollingPartialTitle: "Данные за период неполные.",
				rollingPartial: "Доступное покрытие: {{from}} → {{to}}. Суммы за пределами этого диапазона могут быть занижены.",
				rollingLoading: "Загружаем резервы…",
				rollingError: "Не удалось загрузить резервы за этот период.",
				rollingEmpty: "За этот период данных по резервам нет.",
				reserved: "Зарезервировано за период",
				released: "Освобождено за период",
				held: "Удерживается на конец периода",
				reserveAria: "Резервы {{currency}} за выбранный период",
				earningsTitle: "Доходы клиента · Выбранный период",
				earningsLoading: "Загружаем доходы клиента…",
				earningsError: "Не удалось загрузить доходы клиента за этот период.",
				exportEarningsPdf: "Скачать PDF доходов (все валюты)",
				pdfGenerating: "Формируем PDF…",
				pdfError: "Не удалось сформировать PDF. Повторите попытку.",
				reconciliation: "Сверка",
				earningsView: "Основание расчёта доходов",
				healthTitle: "Проверка стейтментов",
				healthScope: "Текущий клиент · Все даты",
				healthLoading: "Проверяем стейтменты…",
				healthError: "Не удалось проверить стейтменты. Повторите попытку.",
				healthIssues: "Найдены проблемы со стейтментами",
				healthClean: "По этому клиенту проблем со стейтментами не найдено",
				healthThreshold: "Порог зависания: {{count}} дней",
				healthAsOf: "Проверено (время сервера):",
				healthStuck: "Зависшие стейтменты",
				healthOverlapping: "Пересекающиеся пары",
				healthUnbilled: "Дни без стейтмента",
				healthAge: "Возраст, дней",
				healthPeriod: "Период (UTC)",
				healthPair: "Пара",
				healthDays: "Дни",
				accrued: "Начислено",
				paid: "Выплачено",
				deferredTitle: "Отложенные суммы",
				deferredOut: "Выставлено позже периода · Out",
				deferredIn: "Выставлено внутри периода · In",
				deferredOutDescription: "Создано в выбранном периоде, выставлено документом позже.",
				deferredInDescription: "Создано раньше выбранного периода, выставлено документом внутри него.",
				deferredOutHint: "Придёт в следующей выплате.",
				deferredInHint: "Уже выплачено — за более ранние дни. Это не проблема и не входит в красный итог.",
				deferredSubtitle: "края периода",
				deferredNothing: "{{amount}} · ничего",
				byDocuments: "По документам",
				byDocumentsHint: "что выставлено · стейтментов: {{count}} · сходится с банком",
				documentsRounding: "Net отличается от собственной разбивки на {{amount}}: каждый стейтмент — это счёт, и он округляет каждое поле до целых центов отдельно, поэтому документы (их {{count}}) расходятся на цент-другой. Net — это выставленная сумма.",
				oct: "Выплаты OCT",
				byDocumentsSingle: "стейтмент #{{id}} · {{type}} · {{status}}",
				overviewTurnoverDayHint: "Платежей: {{payments}} · созданы {{day}}",
				shiftTitle: "Сдвиг расчёта",
				openPeriod: {
					title: "Период не закрыт — выставлено по {{through}}, {{days}} ещё открыты",
					titleNone: "Период не закрыт — стейтментов ещё нет",
					text: "За эти дни ещё не было сеттла и не было расчёта. Цифры ниже — только по уже выставленным дням.",
					textNone: "За этот период ещё не было сеттла и не было расчёта.",
					notBilled_one: "ещё не выставлено · {{count}} платёж",
					notBilled_few: "ещё не выставлено · {{count}} платежа",
					notBilled_many: "ещё не выставлено · {{count}} платежей",
					notBilled_other: "ещё не выставлено · {{count}} платежа",
				},
				shift: {
					caption: "<b>При T+1 отчёт за день содержит платежи предыдущего дня.</b> Вся разница между {{here}} здесь и {{above}} выше — в этом, обе суммы верны.",
					captionShort: "<b>При T+1 отчёт за день содержит платежи предыдущего дня.</b> Поэтому этот блок отличается от блоков выше — обе картины верны.",
					inDay: "пришло с {{day}}",
					outDay: "ушло на {{day}}",
					inRange: "пришло из дней до периода",
					outRange: "ушло в дни после периода",
				},
				byReportDays: "По дням отчётов",
				byReportDaysHint: "дней: {{count}} · по дате расчёта — другой набор платежей, чем в блоках выше",
				outsideDaysList: "даты вне периода: {{days}}",
				noOutsideDays: "дат вне периода нет",
				total: "Итого",
				reconciledTitle: "Сходится · весь оборот периода разложен без остатка",
				formula: {
					created: "создано",
					deferred: "отложено",
					notSettled: "не засеттлено",
					earlier: "выставлено за ранние дни",
					byDocuments: "по документам",
					outOfReports: "из отчётов",
					intoReports: "в отчёты",
					beyond: "выставлено за концом периода",
					byReportDays: "по дням отчётов",
				},
				notSettled: {
					title: { empty: "Не засеттлено", late: "Не засеттлено · ожидание", lost: "Не засеттлено · потеряно", mixed: "Не засеттлено", open: "Не засеттлено · день не закрыт", stuck: "Не засеттлено · зависло" },
					text: {
						empty: "Ничего не зависло: все платежи периода прошли расчёт.",
						late: "Расчёт ещё не подтверждён. <b>Попадёт в выплату, если эквайер подтвердит</b> — следующий дневной документ подхватит это автоматически.",
						lost: "Расчёт отклонён эквайером. <b>Это никогда не будет выплачено</b> — этих денег нет.",
						mixed: "Часть ещё может прийти, часть не придёт никогда — <b>показаны отдельными строками, не одной суммой</b>.",
						open: "Платежи дней, которые ещё не закрыты стейтментом. <b>Делать ничего не нужно</b> — они попадут в выплату после закрытия дня.",
					},
					row: {
						late: "Не подтверждено",
						lateLong: "Не подтверждено · может прийти",
						lost: "Отклонено",
						lostLong: "Отклонено · не придёт никогда",
						open: "День не закрыт",
						openLong: "День не закрыт · попадёт после закрытия",
					},
					nothing: "{{amount}} · ничего не зависло",
					stuckText: "Старше расчётного окна клиента и всё ещё не подтверждено. <b>Вот это и нужно разбирать.</b>",
					recentOnlyText: "Ничего не зависло: все платежи здесь ещё в пределах расчётного окна клиента.",
					stuckRow: "Расчёт не подтверждён",
					recentTitle: "Ожидают расчёта · норма",
					recentText_one: "созданы за последний {{count}} день — срок ещё не наступил",
					recentText_few: "созданы за последние {{count}} дня — срок ещё не наступил",
					recentText_many: "созданы за последние {{count}} дней — срок ещё не наступил",
					recentText_other: "созданы за последние {{count}} дня — срок ещё не наступил",
					recentTextPlain: "ещё в пределах расчётного окна — срок не наступил",
					recentShow_one: "Показать {{count}} платёж",
					recentShow_few: "Показать {{count}} платежа",
					recentShow_many: "Показать {{count}} платежей",
					recentShow_other: "Показать {{count}} платежа",
					threshold_one: "Считается зависшим после {{count}} дня",
					threshold_other: "Считается зависшим после {{count}} дней",
					thresholdClient_one: "Считается зависшим после {{count}} дня — расчётное окно клиента",
					thresholdClient_other: "Считается зависшим после {{count}} дней — расчётное окно клиента",
				},
				deferredCount: "Записей: {{count}}",
				deferredEmpty: "Нет сумм",
				deferredAtLeast: "{{currency}} · не меньше",
				deferredLowerBound: "Нижняя граница: пока сверка периода не сходится, эти суммы неполные",
				overviewTurnover: "Оборот Overview · итого за период",
				overviewTurnoverHint: "Платежей: {{payments}} · созданы {{from}} → {{to}} · та же сумма, что на экране Overview",
				settlementOpenDay: "день не закрыт",
				settlementStatus: {
					unknown: "расчёт не подтверждён",
					pending: "расчёт в процессе",
					failed: "расчёт отклонён",
					error: "ошибка расчёта",
				},
				unsettledShown: "Показано {{shown}} из {{total}}",
				unsettledAge_zero: "ждёт меньше суток",
				unsettledAge_one: "ждёт {{count}} день",
				unsettledAge_few: "ждёт {{count}} дня",
				unsettledAge_many: "ждёт {{count}} дней",
				unsettledAge_other: "ждёт {{count}} дня",
				openTransaction: "Открыть транзакцию #{{id}}",
				notPaidOutTotal: "Не выплачено за этот период",
				notBilled: "Не выставлено · Not billed",
				notBilledDescription: "Засеттленные транзакции без документа.",
				lateSettledTruncated: "Список поздно засеттленных транзакций неполный. Суммы учитывают все транзакции.",
				notPaidOutBreakdownWithNotBilled: "{{deferred}} отложено + {{unsettled}} не засеттлено + {{notBilled}} не выставлено = <b>{{total}}</b>",
				notPaidOutBreakdown: "{{deferred}} отложено + {{unsettled}} не засеттлено = <b>{{total}}</b>",
				notPaidOutWithheld: "Итог невыплаченного <b>не показан</b>, пока сверка периода не сходится — сумма этих блоков была бы занижена.",
				turnoverMismatch: "Не сходится — разница {{amount}}",
				turnoverMismatchDescription: "Суммы ниже неполные и не складываются в оборот периода. Не используйте их как разбивку того, куда ушли деньги.",
				daysCovered: "Начисления охватывают {{count}} дней",
				paidStatements: "Стейтментов за период: {{count}}",
				paidByPeriod: "Состав выплаты по периодам",
				paidInsidePeriod: "Итого за выбранный период",
				paidOutsidePeriod: "Итого вне выбранного периода",
				outsideDays: "Включённые даты вне периода: {{count}}",
				periodBreakdownDetails: "Детализация показателей",
				fees: "Комиссии",
				rollingTaken: "Удержано в rolling reserve",
				rollingReleased: "Освобождено из rolling reserve",
				refunds: "Возвраты",
				chargebacks: "Чарджбэки",
				net: "Итого",
				withdrawal: "Комиссии за вывод",
				setup: "Setup-комиссии",
				monthly: "Ежемесячные комиссии",
				noActivity: "За этот период активности нет.",
				earningsByChannel: "Доходы по каналам",
				channelsEmpty: "По этой валюте нет данных по каналам.",
				statements: "Оплаченные стейтменты",
				statement: "ID стейтмента",
				openStatement: "Открыть стейтмент №{{id}}",
				type: "Тип",
				status: "Статус",
				period: "Период",
				coverage: "Покрытие",
				fullyCovered: "Полностью внутри",
				partiallyCovered: "Частично",
				statementsEmpty: "По этой валюте нет стейтментов.",
				pendingEmpty: "Нет ожидающих данных",
				pendingAmount: "Ожидает: {{amount}} в ещё не завершённых стейтментах",
				uncoveredDays: "Дней без покрытия: {{count}}",
				fullyReconciled: "Все дни покрыты стейтментами",
				refresh: "Обновить",
				retry: "Повторить",
			},
			sharedEmails: {
				title: "Общие email",
				overviewTitle: "Обзор email",
				overviewIdle: "Общие email и chargeback",
				sharedTab: "У нескольких мерчантов",
				chargebacksTab: "Chargeback",
				chargebacksPeriod: "За последние 3 месяца",
				chargebacksUnavailable: "Данные об email с chargeback пока недоступны.",
				chargebacksLoadError: "Не удалось загрузить email с chargeback.",
				chargebacksCount: "Email с chargeback: {{count}}",
				chargebacksEmpty: "За последние 3 месяца email с chargeback нет.",
				chargebacksSearch: "Поиск по email",
				charged: "Charged",
				returned: "Returned",
				midsCount: "Мерчантов",
				idle: "Email у нескольких мерчантов",
				search: "Email, клиент или мерчант",
				clearSearch: "Очистить поиск",
				matches: "Найдено email: {{count}} из {{total}}",
				noMatches: "Совпадений не найдено.",
				expand: "Развернуть обзор email",
				collapse: "Свернуть обзор email",
				count: "Email у нескольких мерчантов: {{count}}",
				shown: "Показано email: {{count}} из {{total}}",
				truncated: "Неполный список",
				empty: "За выбранный период общих email нет.",
				unavailable: "Данные об общих email пока недоступны.",
				loadError: "Не удалось загрузить общие email.",
				merchant: "Мерчант",
				client: "Клиент",
				attempts: "Попытки",
				completed: "Успешно",
				firstSeen: "Впервые",
				lastSeen: "Последний раз",
			},
			traffic: {
				controlDay: "Контроль трафика · День",
				checking: "Проверка",
				checkingTraffic: "Проверяем трафик…",
				loadError: "Ошибка загрузки",
				normal: "Трафик в норме",
				warning: "Трафик требует внимания",
				critical: "Резкое изменение трафика",
				unavailable: "Нет оценки",
				openDetails: "Открыть детали контроля трафика",
				refreshControl: "Обновить контроль трафика",
				requestError: "Не удалось загрузить данные контроля трафика.",
				comparison: "{{current}} по сравнению с {{previous}}",
				datesPerClient: "Даты сравнения указаны у каждого клиента",
				noProblems: "Отклонений, требующих внимания, не найдено.",
				sharpChange: "Резкое изменение",
				requiresAttention: "Требует внимания",
				change: "Изменение",
				riseFromZero: "Рост с нуля",
				transactions: "Транзакции",
				dataAsOf: "Данные на {{date}} (время сервера)",
				details: "Контроль трафика — детали",
				problems: "Проблемы с трафиком",
				increase: "Рост на {{percent}}%",
				decrease: "Снижение на {{percent}}%",
				noComparison: "Нет данных для сравнения",
				noChange: "Без изменений",
				clientsWithDeviations: "Клиентов с отклонениями: {{count}}",
				noDailyDeviations: "Значимых отклонений за день не найдено",
				insufficientData: "Недостаточно данных для оценки трафика",
			},
			interchange: {
				title: "Комиссии IC++",
				totalAria: "Итого IC++",
				payment: "Платёж",
				inEur: "В EUR",
				exchangeRate: "Курс",
				totalFee: "Общая комиссия IC++",
				ofPaymentAmount: "от суммы платежа",
				groups: {
					interchange: { title: "Межбанковская комиссия", description: "Комиссия банка-эмитента" },
					scheme: { title: "Комиссии платёжной системы", description: "Комиссии платёжной системы" },
					switching: { title: "Switching fees", description: "Обработка транзакции" },
				},
				metadata: { basis: "Основание", source: "Источник", note: "Примечание", id: "ID", service: "Сервис", eventId: "ID события", rate: "Ставка", fixed: "Фикс. часть", maximum: "Максимум", minimum: "Минимум" },
				indicative: "Ориентировочно",
				feeDetails: "Условия и источник комиссии",
				emptyCategory: "В расчёте нет комиссий этой категории.",
				loadError: "Не удалось загрузить расчёт IC++. Попробуйте обновить данные.",
				loading: "Загружаем расчёт IC++…",
				displayError: "Не удалось отобразить расчёт IC++. Исходный ответ доступен по кнопке Raw.",
				unavailable: "Расчёт IC++ для этого платежа недоступен.",
			},
			transactionAnalytics: {
				targets: { bank: "Банк", email: "Эл. почта", card: "Карта" },
				analysisObject: "Объект аналитики",
				period: "Период аналитики",
				fromDate: "С даты",
				toDate: "По дату",
				show: "Показать",
				invalidPeriod: "Укажите корректный период: дата начала не должна быть позже даты окончания.",
				loadingTransaction: "Загружаем данные транзакции…",
				wrongProject: "Откройте транзакцию в её проекте, чтобы посмотреть аналитику.",
				analysisFor: "Анализ: {{name}}",
				periodField: "Период",
				currencyField: "Валюта",
				loadError: "Не удалось загрузить аналитику.",
				loadingAnalysis: "Загружаем аналитику…",
				prepareError: "Не удалось подготовить аналитику за выбранный период. Попробуйте обновить данные. Исходные данные доступны по кнопке Raw.",
				missing: { bank: "В транзакции не указан банк.", email: "В транзакции нет email плательщика.", card: "В транзакции нет токена карты для анализа." },
				paymentCard: "Карта платежа",
			},
			emailInfo: {
				summary: "Активность email", loading: "Загружаем активность email…", loadError: "Не удалось загрузить дополнительные сведения об email.",
				periodBasis: "Активность за выбранный период учитывается по дате создания платежа.",
				attempts: "Попытки", completed: "Завершены", failed: "Неуспешные", abandoned: "Незавершённые", charged: "Charged", returned: "Returned", refunded: "Возвраты",
				cards: "Карты", mids_count: "MID", clients_count: "Клиенты", currencies: "Валюты", ips: "IP-адреса", countries: "Страны", instruments: "Инструменты", distinct_names: "Уникальные имена",
				first_seen: "Впервые", last_seen: "Последняя активность", names: "Имена плательщика", amounts: "Суммы по валютам", currency: "Валюта",
				completed_amount: "Сумма завершённых", failed_amount: "Сумма неуспешных", charged_amount: "Сумма Charged", refunded_amount: "Сумма возвратов",
				clients: "Клиенты и MID", unnamedClient: "Клиент без названия", unnamedCard: "Номер карты не указан",
				identities: "Данные плательщика", identity: "Плательщик", samplesNote: "Примеры данных плательщика, полученные для этого MID; список может включать не все платежи.",
				created_at: "Платёж создан", name: "Имя", email: "Эл. почта", countryCode: "Код страны", ip: "IP-адрес", phone: "Телефон", city: "Город", state: "Регион", postalCode: "Почтовый индекс", addressLine1: "Адрес", addressLine2: "Адрес (строка 2)", birthDate: "Дата рождения",
				noAmounts: "Суммы не переданы.", noClients: "Клиенты не переданы.", noMids: "MID не переданы.", noIdentities: "Данные плательщика не переданы.", noCards: "Карты не переданы.",
				payments: "Платежи", conversion: "Конверсия", volume: "Объём", linked: "Связанные данные", activity: "Активность",
				attemptsCount: "Попытки: {{value}}", completedOf: "Завершено {{completed}} из {{attempts}}", completedVolume: "завершено", otherStatuses: "Другие статусы",
				afterCompletion: "После завершения", shareOfCompleted: "Доля от завершённых", averagePayment: "Средний завершённый платёж",
				midsCount: "MID: {{value}}", activeDays: "{{value}} дн.", to: "по", flagged: "Требует внимания",
				verdict: { danger: "Есть чарджбэки", warn: "Требует внимания", ok: "Сигналов риска нет", empty: "Нет платежей за период" },
				verdictHint: { ok: "Проверены: чарджбэки, возвраты, конверсия, карты, клиенты, имена плательщика и страны.", empty: "За выбранный период активности по этому email нет." },
				signalsLabel: "Сигналы", signalHint: "Подсвечивается от {{value}}", conversionHint: "Ниже {{rate}} при {{attempts}} и более попытках",
				signals: { charged: "Charged: {{value}}", returned: "Returned: {{value}}", refunded: "Возвраты: {{value}}", conversion: "Низкая конверсия: {{value}}", cards: "Карты: {{value}}", clients_count: "Клиенты: {{value}}", distinct_names: "Имена плательщика: {{value}}", countries: "Страны: {{value}}" },
			},
			bankAnalysis: {
				rules: "Правила", rulesEmpty: "За выбранный период блокировок правилами для этого email нет.",
				titles: { bank: "Анализ банка · {{name}}", country: "Анализ страны · {{name}}", email: "Анализ email · {{name}}", card: "Анализ карты · {{name}}" },
				transactions: "Транзакции",
				loadingDetails: "Загружаем детали…",
				loadError: "Не удалось загрузить анализ.",
				responseMismatch: "Не удалось сопоставить ответ с выбранным объектом и периодом или распознать показатели. Исходный ответ доступен во вкладке Raw.",
				crmResponse: "Ответ CRM",
				waitingResponse: "Ожидаем ответ…",
				rawUnavailable: "Исходный ответ недоступен.",
				allCurrencies: "Все валюты",
				periods: { today: "Сегодня", yesterday: "Вчера", dayBeforeYesterday: "Позавчера", thisMonth: "Этот месяц", lastMonth: "Прошлый месяц", lastDays: "Последние {{count}} дней", lastDaysThroughYesterday: "Последние {{count}} дней по вчерашний день" },
				declines: "Отказов",
				declinesAria: "отказов",
				declinesCount: "Количество отказов",
				shareOfDeclines: "Доля всех отказов",
				totalDeclines: "всего отказов",
				countUnit: "шт.",
				reasons: { title: "Причины отказов", reason: "Причина", google_pay_token: "Отсутствует или некорректен токен Google Pay", wrong_params: "Некорректные параметры запроса", wrong_pan: "Ошибка BIN: некорректный номер карты", no_channel: "Нет активного канала для терминала", fraud: "Подозрение на мошенничество", funds: "Недостаточно средств или превышен кредитный лимит", conditions: "Ограничения банка по карте", security: "Отказ по требованиям безопасности", cardholder: "Блокировка держателем карты — требуется связаться с ним", no_card: "Карта не найдена при аутентификации", pending: "Платёж ещё обрабатывается", invalid_card: "Некорректный номер карты", expired_card: "Срок действия карты истёк", "3ds": "Не пройдена аутентификация 3-D Secure" },
				actions: { google_pay_token: "Проверьте получение токена Google Pay и его передачу в платёжный запрос: CRM сообщает об отсутствующем или некорректном токене.", wrong_params: "Проверьте обязательные параметры платёжного запроса и их формат.", wrong_pan: "Проверьте номер карты и параметры, переданные для определения BIN.", no_channel: "Проверьте активные каналы и настройки маршрутизации терминала.", fraud: "Проверьте причины срабатывания антифрода у провайдера и банка. Эта причина отказа сама по себе не подтверждает мошенничество.", funds: "Проверьте повторные попытки оплаты: клиенту может потребоваться пополнить счёт или использовать другую карту.", conditions: "Уточните у провайдера или банка, какие ограничения по карте вызвали отказы.", security: "Уточните у провайдера или банка, какие проверки безопасности отклоняют платежи.", "3ds": "Проверьте прохождение 3-D Secure и ответы сервиса аутентификации банка.", pending: "Проверьте финальные статусы: CRM включила в отказы платежи, которые ещё обрабатываются.", fallback: "Проверьте сообщения об отказах и повторные попытки по картам в деталях ниже." },
				summary: { noDeclines: "За выбранный период отказов не найдено.", noBreakdown: "CRM вернула отказов: {{total}}. Разбивка по причинам отсутствует.", topReason: "Самая частая причина в ответе — «{{reason}}»: {{count}} из {{total}} отказов ({{share}})." },
				countries: "Страны",
				country: "Страна",
				banks: "Банки",
				bank: "Банк",
				otherCountries: "Другие страны",
				otherBanks: "Другие банки",
				otherReasons: "Другие причины",
				notProvided: "Не указано в ответе",
				countryNotProvided: "Страна не указана",
				bankNotProvided: "Банк не указан",
				cardNotProvided: "Карта не указана",
				emailNotProvided: "Email не указан",
				allCountries: "Все страны ({{count}})",
				allBanks: "Все банки ({{count}})",
				allReasons: "Все причины ({{count}})",
				collapse: "Свернуть",
				noPeriodDeclines: "За выбранный период отказов нет.",
				highFrequency: "Высокая частота отказов",
				requiresAttention: "Требует внимания",
				highFrequencyAria: "высокая частота",
				requiresAttentionAria: "требует внимания",
				cardsWithDeclines: "Карты с отказами",
				emailsWithDeclines: "Email с отказами",
				card: "Карта",
				email: "Эл. почта",
				attentionCount: "Требуют внимания: {{count}}",
				noThresholdItems: "нет позиций, достигших порога",
				periodUnknown: "период не определён",
				crmItems: "В ответе CRM: {{count}} позиций.",
				attentionThreshold: "Порог внимания: от {{count}} отказов за {{days}} дн.",
				periodThresholdUnavailable: "Не удалось определить период для оценки повторов.",
				noCardsAtThreshold: "В присланном топе нет карт, достигших порога внимания.",
				noEmailsAtThreshold: "В присланном топе нет email, достигших порога внимания.",
				unknownDeclines: "{{label}}: {{count}} отказов. Их нельзя отнести к одному объекту.",
				attentionOnly: "Только требующие внимания",
				showFullTop: "Показать весь топ ({{count}})",
				partialReasons: "В разбивке указаны причины для {{count}} из {{total}} отказов. Остальные причины CRM не передала.",
			},
			analysis: {
				ok: "Всё в порядке",
				warning: "Требует внимания",
				critical: "Есть проблемы",
				unavailable: "Оценка пока недоступна",
				title: "Анализ платежей",
				filters: {
					category: "Фильтр по типу записи", severity: "Фильтр по важности",
					all: "Все типы", bank: "Банки", country: "Страны", channel: "Платёжные каналы", email: "Эл. почта", technical: "Технические ошибки", other: "Прочие записи",
					allLevels: "Любая важность", critical: "Критичные", warning: "Предупреждения", info: "Наблюдения / мало данных",
					matches: "Найдено {{count}} из {{total}}", reset: "Сбросить фильтры", empty: "Нет записей с такими фильтрами.",
				},
				loadError: "Ошибка загрузки",
				analyzing: "Анализируем статистику…",
				noSignificantIssues: "Значимых отклонений не найдено",
				expand: "Развернуть",
				collapse: "Свернуть",
				expandRules: "Развернуть анализ правил",
				collapseRules: "Свернуть анализ правил",
				observations: "Наблюдения",
				criticalLabel: "Критично",
				warningLabel: "Внимание",
				infoLabel: "Наблюдение",
				smallSample: "Мало данных",
				changeFromPrevious: "Изменение к предыдущему периоду",
				decreasePoints: "Снижение на {{value}} п. п.",
				belowTarget: "при норме 80%",
				conversion: "проходимость",
				paymentAttempts: "попыток оплаты",
				fewerThan30: "менее 30",
				errors: "ошибок",
				showTechnicalErrors: "Показать технические ошибки",
				openAnalysis: "Открыть анализ: {{name}}",
				openAnalysisBank: "Открыть анализ банка {{name}}",
				openAnalysisCountry: "Открыть анализ страны {{name}}",
				openAnalysisEmail: "Открыть анализ email {{name}}",
				technicalErrors: "Технические ошибки",
				totalMessages: "Всего сообщений: {{count}}",
				statisticsMessage: "Сообщение из статистики",
				count: "Количество",
				technicalCategories: {
					google_pay_token: "Неверный формат токена Google Pay",
					routing: "Нет активного канала для терминала",
					xml: "Ошибки разбора XML в платёжной интеграции",
				},
				redactedEmail: "[скрыто]",
				redactedLink: "[ссылка]",
				redactedNumber: "[номер]",
				technicalErrorsHint: "Ошибки из той же статистики, на которой основано предупреждение. Сообщения могут быть сокращены; список отдельных транзакций в этих данных отсутствует.",
				moreIssues: "ещё проблем: {{count}}",
				criticalPrefix: "Критично",
				noIssues: "Значимых отклонений нет",
				observationAvailable: "есть наблюдение",
				showAll: "Показать все ({{count}})",
				collapseToTen: "Свернуть до 10",
				observation: "Наблюдение.",
			},
		},
	},
} as const;

const storedLanguage = (() => {
	try {
		const value = localStorage.getItem(LANGUAGE_STORAGE_KEY);
		return SUPPORTED_LANGUAGES.includes(value as AppLanguage) ? (value as AppLanguage) : "en";
	} catch {
		return "en";
	}
})();

void i18n.use(initReactI18next).init({
	resources,
	lng: storedLanguage,
	fallbackLng: "en",
	supportedLngs: SUPPORTED_LANGUAGES,
	interpolation: { escapeValue: false },
	returnNull: false,
});

const syncDocumentLanguage = (language: string) => {
	const normalized = language.startsWith("ru") ? "ru" : "en";
	document.documentElement.lang = normalized;
	try {
		localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
	} catch {
		// Language still changes for the current session when storage is unavailable.
	}
};

syncDocumentLanguage(storedLanguage);
i18n.on("languageChanged", syncDocumentLanguage);

export const getAppLanguage = (): AppLanguage => i18n.resolvedLanguage === "ru" ? "ru" : "en";

export default i18n;
